// iframe-xframe-detector.js - 专门用于检测X-Frame-Options限制的工具

/**
 * XFrameDetector - 用于检测和处理iframe的X-Frame-Options限制
 * 可以全局监听所有iframe的X-Frame-Options错误并触发回调
 */
class XFrameDetector {
  constructor() {
    this.callbacks = [];
    this.isInitialized = false;
  }

  /**
   * 初始化检测器
   * 监听window错误事件和iframe加载事件
   */
  init() {
    if (this.isInitialized) return;
    
    // 监听window错误事件 - 可以捕获一些iframe加载错误
    window.addEventListener('error', this.handleWindowError.bind(this), true);
    
    // 监听document错误事件 - 捕获资源加载错误
    document.addEventListener('error', this.handleDocumentError.bind(this), true);
    
    this.isInitialized = true;
    console.log('X-Frame-Options检测器已初始化');
  }

  /**
   * 处理window错误事件
   */
  handleWindowError(event) {
    // 检查错误消息中是否包含X-Frame-Options相关内容
    if (event.error && event.error.message && 
        event.error.message.includes('X-Frame-Options')) {
      console.log('捕获到X-Frame-Options错误:', event.error.message);
      this.notifyCallbacks(null, true);
    }
  }

  /**
   * 处理document错误事件
   */
  handleDocumentError(event) {
    // 检查是否是iframe元素的错误
    if (event.target && event.target.tagName === 'IFRAME') {
      const iframe = event.target;
      console.log('捕获到iframe错误事件:', iframe.src);
      this.notifyCallbacks(iframe, true);
    }
  }

  /**
   * 为特定iframe添加X-Frame-Options检测
   * @param {HTMLIFrameElement} iframe - iframe元素
   * @param {string} url - 要检测的URL
   * @param {Function} callback - 检测结果回调函数
   */
   detectForIframe(iframe, url, callback) {
     // 保存回调
     const callbackId = this.addCallback(callback);
     
     // 添加直接错误监听
     const errorHandler = () => {
       console.log('直接捕获到iframe错误:', url);
       this.removeCallback(callbackId);
       callback(iframe, true);
     };
     
     iframe.addEventListener('error', errorHandler, { once: true });
     
     // 添加加载后检测
     const loadHandler = () => {
       setTimeout(() => {
         this.checkIframeContent(iframe, url, (hasRestriction) => {
           iframe.removeEventListener('error', errorHandler);
           this.removeCallback(callbackId);
           callback(iframe, hasRestriction);
         });
       }, 100); // 与其他检测逻辑保持一致的延迟时间
     };
     
     iframe.addEventListener('load', loadHandler, { once: true });
   }

   /**
    * 检查iframe内容是否为空或被X-Frame-Options限制
    */
   checkIframeContent(iframe, url, callback) {
     try {
       // 尝试访问iframe内容 - 如果被CORS阻止会抛出异常
       if (iframe.contentDocument) {
         // 检查document是否为空或只有错误信息
         const doc = iframe.contentDocument;
         
         // 检查整个document的textContent
         const allText = doc.textContent || '';
         const isEmptyDoc = allText.trim() === '';
         
         // 检查body
         if (doc.body) {
           const body = doc.body;
           const isEmptyBody = body.children.length === 0 || 
                              (body.innerText && body.innerText.trim() === '');
           
           // 检查是否有错误相关文本
           const hasErrorText = allText.includes('X-Frame-Options') || 
                               allText.includes('Refused to display') ||
                               allText.includes('Cannot display') ||
                               allText.includes('permission denied');
           
           if (isEmptyBody || isEmptyDoc || hasErrorText) {
             console.log('检测到X-Frame-Options限制:', url);
             callback(iframe, true);
             return;
           }
         } else if (isEmptyDoc) {
           // 没有body且文档为空，也视为被限制
           console.log('检测到X-Frame-Options限制（无body内容）:', url);
           callback(iframe, true);
           return;
         }
       }
       
       // 尝试访问location.href，这通常在X-Frame-Options限制时会失败
       try {
         const href = iframe.contentWindow.location.href;
         // 如果能访问href，但内容为空，也视为被限制
         const doc = iframe.contentDocument;
         if (doc && doc.body && doc.body.children.length === 0) {
           console.log('检测到X-Frame-Options限制（内容为空）:', url);
           callback(iframe, true);
           return;
         }
       } catch (e) {
         console.log('检测到X-Frame-Options限制（无法访问location）:', e.message);
         callback(iframe, true);
         return;
       }
       
       // 内容不为空且无错误，可能没有限制
       console.log('未检测到X-Frame-Options限制:', url);
       callback(iframe, false);
     } catch (error) {
       // 捕获到CORS异常，说明可能有X-Frame-Options或CORS限制
       console.log('检测到CORS/X-Frame-Options限制:', error.message);
       callback(iframe, true);
     }
   }

  /**
   * 添加回调函数
   */
  addCallback(callback) {
    const id = Date.now() + Math.random();
    this.callbacks.push({ id, callback });
    return id;
  }

  /**
   * 移除回调函数
   */
  removeCallback(id) {
    this.callbacks = this.callbacks.filter(cb => cb.id !== id);
  }

  /**
   * 通知所有回调函数
   */
  notifyCallbacks(iframe, hasRestriction) {
    this.callbacks.forEach(({ callback }) => {
      try {
        callback(iframe, hasRestriction);
      } catch (error) {
        console.error('执行X-Frame-Options回调时出错:', error);
      }
    });
  }
}

// 创建单例实例
const xFrameDetector = new XFrameDetector();

// Vue插件安装函数
export function install(Vue) {
  // 添加全局方法
  Vue.prototype.$xFrameDetector = xFrameDetector;
  Vue.xFrameDetector = xFrameDetector;
  
  // 创建自定义指令 v-xframe-detect
  Vue.directive('xframe-detect', {
    inserted(el, binding) {
      if (el.tagName !== 'IFRAME') {
        console.warn('v-xframe-detect指令只能应用于iframe元素');
        return;
      }
      
      // 初始化检测器
      xFrameDetector.init();
      
      // 获取回调函数
      const callback = binding.value || function(iframe, hasRestriction) {
        if (hasRestriction && binding.arg) {
          // 如果提供了事件名，触发事件
          el.dispatchEvent(new CustomEvent(binding.arg));
        }
      };
      
      // 监听iframe的load和error事件
      el.addEventListener('load', () => {
        // 添加延迟以确保有足够时间让错误信息反映在iframe中
        setTimeout(() => {
          xFrameDetector.checkIframeContent(el, el.src, callback);
        }, 100);
      });
      
      el.addEventListener('error', () => {
        callback(el, true);
      });
    }
  });
}

// 导出检测器实例
export default xFrameDetector;




